/**
 * AIEscalationSection — Universal escalation / handoff configuration for all AI node editors.
 *
 * Used identically by: BigCommerceAIEditor, TransferToAIEditor, HubSpotAIEditor,
 *                       NetsuiteAIEditor, DynamicsAIEditor
 *
 * How it works:
 *   Each escalation path (agent transfer, SMS request, timeout fallback) maps to
 *   a named jumpTarget on the node. At runtime the AI is given a tool named
 *   jump_to_{section_name}(reason: string). When the AI detects the matching intent
 *   it calls the tool and workflow execution jumps to the configured Section Entry node.
 *
 *   Department routing (escalationTeamId) is stored as a separate node field and passed
 *   to the Transfer to Agent node in the target section at runtime.
 */
import React, { useContext, useMemo } from "react";
import { WorkflowContext } from "@/context/WorkflowContext";
import { WorkflowNode, WorkflowJumpTarget } from "../../types";
import useTeams from "@/hooks/useTeams";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

/** Well-known escalation jump-target names the runtime recognises. */
const ESCALATION_SLOTS = [
  {
    id: "agent_transfer",
    label: "Transfer to Agent",
    icon: "👤",
    color: "blue",
    defaultWhen:
      "customer asks to speak with a person, requests a human agent, or the AI cannot resolve the issue after 2 attempts",
    description:
      "Route to a live agent when the AI can't help or the customer asks for one.",
    teamPickerLabel: "Route to team (optional)",
    teamPickerHelp:
      "Leave blank to use the default team. Routing to a specific team is gated per subscription.",
  },
  {
    id: "sms_request",
    label: "Send SMS Update",
    icon: "💬",
    color: "green",
    defaultWhen:
      "customer asks to receive a text message, wants a confirmation SMS, or requests an update via SMS",
    description: "Jump to an SMS-sending section when the customer wants a text.",
    teamPickerLabel: null,
    teamPickerHelp: null,
  },
  {
    id: "timeout_fallback",
    label: "Idle / Timeout Fallback",
    icon: "⏱",
    color: "amber",
    defaultWhen:
      "conversation is idle for too long, or the AI has been unable to resolve after several turns",
    description:
      "Where to go when the conversation stalls. Good for sending a follow-up SMS or ending gracefully.",
    teamPickerLabel: null,
    teamPickerHelp: null,
  },
];

const COLOR_MAP: Record<string, string> = {
  blue: "border-blue-300 bg-blue-50",
  green: "border-green-300 bg-green-50",
  amber: "border-amber-300 bg-amber-50",
};

export default function AIEscalationSection({ node, onUpdate }: Props) {
  const { workflow } = useContext(WorkflowContext);
  const { teamOptions } = useTeams();

  const sectionNodes = useMemo(() => {
    if (!workflow) return [];
    return Object.values(workflow.nodes).filter(
      (n) => n.type === "action" && n.actionType === "workflowSection" && n.id !== node.id
    );
  }, [workflow, node.id]);

  const jumpTargets: WorkflowJumpTarget[] = Array.isArray(node.jumpTargets)
    ? node.jumpTargets
    : [];

  /** Which jumpTarget is wired for a given escalation slot */
  function getTarget(slotId: string): WorkflowJumpTarget | undefined {
    return jumpTargets.find((t) => t.name === slotId);
  }

  function enableSlot(slotId: string, sectionNodeId: number, when: string) {
    const sectionNode = sectionNodes.find((n) => n.id === sectionNodeId);
    if (!sectionNode) return;
    const filtered = jumpTargets.filter((t) => t.name !== slotId);
    const next: WorkflowJumpTarget[] = [
      ...filtered,
      { nodeId: sectionNodeId, name: slotId, when },
    ];
    onUpdate({ jumpTargets: next });
  }

  function disableSlot(slotId: string) {
    const next = jumpTargets.filter((t) => t.name !== slotId);
    onUpdate({ jumpTargets: next.length > 0 ? next : undefined });
  }

  function updateWhen(slotId: string, when: string) {
    const next = jumpTargets.map((t) => (t.name === slotId ? { ...t, when } : t));
    onUpdate({ jumpTargets: next });
  }

  function updateNodeId(slotId: string, nodeId: number) {
    const existing = jumpTargets.find((t) => t.name === slotId);
    const next = jumpTargets.map((t) =>
      t.name === slotId ? { ...t, nodeId } : t
    );
    if (!existing) return;
    onUpdate({ jumpTargets: next });
  }

  // teamOptions comes directly from TeamsContext (already memoised)

  const hasNoSections = sectionNodes.length === 0;

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
          Escalation &amp; Handoff
        </span>
        <span className="text-xs text-slate-400 italic">recommended</span>
      </div>
      <p className="text-xs text-slate-500 mb-3 leading-relaxed">
        Configure what happens when the AI can't resolve an issue, the customer asks for help,
        or the conversation times out. Each path jumps to a{" "}
        <span className="font-medium text-slate-700">Section Entry</span> node in this workflow.
      </p>

      {hasNoSections && (
        <div className="mb-3 px-3 py-2.5 rounded border border-amber-200 bg-amber-50 text-xs text-amber-800">
          <span className="font-semibold">No Section Entry nodes yet.</span> Add{" "}
          <span className="font-medium">Section Entry</span> nodes to your workflow (e.g.
          "Transfer to Agent", "Send SMS") to enable escalation paths below.
        </div>
      )}

      <div className="flex flex-col gap-2">
        {ESCALATION_SLOTS.map((slot) => {
          const target = getTarget(slot.id);
          const isEnabled = !!target;
          const activeColors = COLOR_MAP[slot.color] ?? "border-slate-300 bg-slate-50";

          return (
            <div
              key={slot.id}
              className={`rounded border p-2.5 transition-colors ${
                isEnabled ? activeColors : "border-slate-200 bg-white"
              }`}
            >
              {/* Toggle header */}
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 mt-0.5 shrink-0"
                  checked={isEnabled}
                  disabled={!isEnabled && hasNoSections}
                  onChange={(e) => {
                    if (e.target.checked) {
                      // Pick the first available section node as default
                      const first = sectionNodes[0];
                      if (first) enableSlot(slot.id, first.id, slot.defaultWhen);
                    } else {
                      disableSlot(slot.id);
                    }
                  }}
                  aria-label={`Enable ${slot.label} escalation`}
                />
                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                  <span className="text-sm font-medium text-slate-800">
                    {slot.icon} {slot.label}
                  </span>
                  <span className="text-xs text-slate-500">{slot.description}</span>
                </div>
              </label>

              {/* Expanded config when enabled */}
              {isEnabled && (
                <div className="mt-2.5 ml-6.5 flex flex-col gap-2">
                  {/* Section picker */}
                  <div>
                    <label className="text-xs font-medium text-slate-700 block mb-1">
                      Jump to section
                    </label>
                    <select
                      className="w-full text-xs border border-slate-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                      value={target.nodeId}
                      onChange={(e) => updateNodeId(slot.id, Number(e.target.value))}
                      aria-label="Select target section"
                    >
                      {sectionNodes.map((sn) => (
                        <option key={sn.id} value={sn.id}>
                          {sn.sectionName || sn.name || `Section ${sn.id}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* When condition */}
                  <div>
                    <label className="text-xs font-medium text-slate-700 block mb-1">
                      Trigger this when the customer…
                    </label>
                    <input
                      type="text"
                      className="w-full text-xs border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      value={target.when ?? ""}
                      onChange={(e) => updateWhen(slot.id, e.target.value)}
                      placeholder={slot.defaultWhen}
                      aria-label="Intent trigger description"
                    />
                    <span className="text-xs text-slate-400 mt-0.5 block">
                      The AI uses this to know when to escalate.
                    </span>
                  </div>

                  {/* Team picker — only for agent_transfer */}
                  {slot.teamPickerLabel && teamOptions.length > 0 && (
                    <div>
                      <label className="text-xs font-medium text-slate-700 block mb-1">
                        {slot.teamPickerLabel}
                      </label>
                      <select
                        className="w-full text-xs border border-slate-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                        value={(node as WorkflowNode & { escalationTeamId?: number }).escalationTeamId ?? ""}
                        onChange={(e) =>
                          onUpdate({
                            escalationTeamId: e.target.value ? Number(e.target.value) : undefined,
                          } as Partial<WorkflowNode>)
                        }
                        aria-label="Select agent team for escalation"
                      >
                        <option value="">— Default team —</option>
                        {teamOptions.map((t) => (
                          <option key={String(t.value)} value={String(t.value)}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                      {slot.teamPickerHelp && (
                        <span className="text-xs text-slate-400 mt-0.5 block">
                          {slot.teamPickerHelp}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Tip about returning control */}
      {jumpTargets.some((t) => ESCALATION_SLOTS.map((s) => s.id).includes(t.name)) && (
        <p className="text-xs text-slate-400 mt-2 leading-relaxed">
          Tip: Add a <span className="font-medium text-slate-600">Transfer to AI</span> node at
          the end of each section to return control to this AI after escalation completes.
        </p>
      )}
    </div>
  );
}
